<script lang="ts" setup>
import AuthLayout from '@/layouts/AuthLayout.vue';
import ReportFilter from "@/components/features/reports/ReportFilter.vue";
import SalesByCustomerReportDialog from "@/components/features/reports/SalesByCustomerReportDialog.vue";
import { getSalesByCustomer } from '@/api/reports';
import { getCustomerList } from '@/api/customers';
import { ref, type Ref, onMounted, computed, watch } from "vue";
import ProgressSpinner from 'primevue/progressspinner';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import type { SalesByCustomerReportResponse, SalesByCustomerReportParams } from '@/types/report-types';
import type { Customer } from '@/types/customer-types';

const toast = useToast();

const selectedCustomerId = ref<string | null>(null);
const startDate = ref<Date | null>(null);
const endDate = ref<Date | null>(null);

const reportData: Ref<SalesByCustomerReportResponse | null> = ref(null);
const isLoading = ref(false);
const isLoadingCustomers = ref(false);
const errorMessage = ref<string | null>(null);
const showReportDialog = ref(false);

const fetchedCustomers = ref<Customer[]>([]);

const customerOptions = computed(() => {
  const options: { label: string; value: string }[] = [];

  if (fetchedCustomers.value.length > 0) {
    fetchedCustomers.value
        .slice()
        .sort((a, b) =>
            ((a.businessName ?? a.id ?? '').localeCompare(b.businessName ?? b.id ?? ''))
        )
        .forEach(customer => {
          options.push({ label: customer.businessName || `ID: ${customer.id}`, value: String(customer.id) });
        });
  }
  return options;
});

onMounted(async () => {
  isLoadingCustomers.value = true;
  try {
    fetchedCustomers.value = await getCustomerList();
    if (customerOptions.value.length > 0 && !selectedCustomerId.value) {
      selectedCustomerId.value = customerOptions.value[0].value;
    }
  } catch (error: any) {
    console.error("Error fetching customers:", error);
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load customer options.', life: 3000 });
  } finally {
    isLoadingCustomers.value = false;
  }
});

watch(customerOptions, (newOptions) => {
  if (newOptions.length > 0 && selectedCustomerId.value === null) {
    selectedCustomerId.value = newOptions[0].value;
  } else if (newOptions.length === 0) {
    selectedCustomerId.value = null;
  }
}, { immediate: true });

const handleGenerateReport = async () => {
  if (!startDate.value || !endDate.value) {
    toast.add({ severity: 'warn', summary: 'Validation Error', detail: 'Please select both a start and end date.', life: 3000 });
    return;
  }
  if (!selectedCustomerId.value) {
    toast.add({ severity: 'warn', summary: 'Validation Error', detail: 'Please select a customer.', life: 3000 });
    return;
  }

  isLoading.value = true;
  errorMessage.value = null;
  reportData.value = null;

  try {
    const params: SalesByCustomerReportParams = {
      startDate: startDate.value.toISOString(),
      endDate: endDate.value.toISOString(),
      customerId: selectedCustomerId.value,
    };

    const data = await getSalesByCustomer(params);

    if (data && data.packingLists) {
      reportData.value = data;
      if (data.packingLists.length > 0) {
        showReportDialog.value = true;
      } else {
        toast.add({ severity: 'info', summary: 'No Data', detail: 'No sales data found for the selected customer and date range.', life: 4000 });
        showReportDialog.value = false;
      }
    } else {
      reportData.value = null;
      toast.add({ severity: 'info', summary: 'No Data', detail: 'No sales data found for the selected criteria (empty response).', life: 4000 });
      showReportDialog.value = false;
    }

  } catch (error: any) {
    console.error("Error fetching Sales by Customer report:", error);
    const apiErrorMessage = error.response?.data?.title || error.response?.data?.message || error.message || "Failed to fetch report data.";
    errorMessage.value = apiErrorMessage;
    toast.add({ severity: 'error', summary: 'API Error', detail: apiErrorMessage, life: 5000 });
    showReportDialog.value = false;
  } finally {
    isLoading.value = false;
  }
};

const handleCloseReportDialog = () => {
  showReportDialog.value = false;
};
</script>

<template>
  <AuthLayout>
    <div class="min-h-screen p-4 md:p-6">
      <ReportFilter
          title="Sales by Customer"
          dropdownLabel="Customer"
          v-model="selectedCustomerId"
          :dropdownOptions="customerOptions"
          :disabled="isLoadingCustomers || customerOptions.length === 0"
          placeholder="Select a Customer"
          @update:startDate="startDate = $event"
          @update:endDate="endDate = $event"
          @generate-report="handleGenerateReport"
      />

      <div v-if="isLoading || isLoadingCustomers" class="flex justify-center items-center mt-10">
        <ProgressSpinner strokeWidth="4" style="width: 50px; height: 50px" animationDuration=".5s"/>
        <p class="ml-3 text-lg text-gray-600">
          {{ isLoadingCustomers ? 'Loading customer options...' : 'Generating Report...' }}
        </p>
      </div>

      <div v-if="!isLoadingCustomers && customerOptions.length === 0 && !isLoading" class="mt-6 text-center text-orange-600">
        <p>No customers available to generate a report. Please ensure customers exist in the system.</p>
      </div>

      <Message v-if="errorMessage && !showReportDialog" severity="error" :closable="true" class="mt-6">
        {{ errorMessage }}
      </Message>

      <div v-if="!isLoading && !isLoadingCustomers && !reportData && !errorMessage && !showReportDialog && customerOptions.length > 0" class="mt-10 text-center text-gray-500">
        <p>Select your criteria and click "Create Report" to view details.</p>
      </div>

      <SalesByCustomerReportDialog
          :visible="showReportDialog"
          :reportData="reportData"
          :startDate="startDate"
          :endDate="endDate"
          :customerId="selectedCustomerId"
          :onClose="handleCloseReportDialog"
      />
    </div>
  </AuthLayout>
</template>
